import { faBuilding, faDesktop, faFileLines, faList, faPuzzlePiece, faShieldHalved, faUser, faUsers } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Add from "@mui/icons-material/Add";
import Close from "@mui/icons-material/Close";
import Delete from "@mui/icons-material/Delete";
import Edit from "@mui/icons-material/Edit";
import Search from "@mui/icons-material/Search";
import { Box, FormControlLabel, IconButton, InputAdornment, Stack, Switch, Typography } from "@mui/material";

import TagManagement from "../../../tags/TagManagement";
import { CoAppDataGridHeaderTitle } from "../../styled/global.styled";
import CoAppIconActionButton from "../inputs/CoAppIconActionButton";
import CoAppIconTextButton from "../inputs/CoAppIconTextButton";
import CoAppTextField from "../inputs/CoAppTextField";

// ─────────────────────────────────────────────────────────────────────────────
// CoAppDatagridHeader — page-level header strip for every list page (Rules,
// Users, Roles, Groups, Plugins, Workstations).
//
// Layout (gestalt):
//   • Title sits at typographic h1 size on the left
//   • Optional description renders directly under the title (tight spacing,
//     muted color so it reads as supporting metadata)
//   • Primary action ("Build Rule" / "Add User" / etc.) hugs the title's
//     baseline so the eye reads them as a single command unit
//   • Secondary actions / toggle drift to the right edge
//   • Hairline divider closes the header off so the search row + grid
//     register as a separate plane visually
// ─────────────────────────────────────────────────────────────────────────────

// Page-title icons mirror the sidebar nav icons so the menu item and its page
// share the same glyph. Filled with the nav's teal→blue gradient (the
// `navIconGradient` def is always mounted via the sidebar).
const PAGE_TITLE_ICONS = {
	rules: faFileLines,
	organization: faBuilding,
	users: faUser,
	groups: faUsers,
	roles: faShieldHalved,
	workstations: faDesktop,
	plugins: faPuzzlePiece,
	collections: faList,
};

export default function CoAppDatagridHeader(props) {
	const {
		title, resourceType, addOnClickHandler,
		editOnClickHandler, deleteOnClickHandler, addIsPresent,
		editIsPresent, deleteIsPresent, subResourceType,
		hasDescription = false, description = "", tagManagementIsPresent = false,
		toggleIsPresent = false, toggleHandler = "", toggleStatus = false, initRuleLibrary,
		searchIsPresent = false, searchValue = "", onSearchChange, searchPlaceholder = "",
		// Optional control rendered immediately to the right of the search
		// field (e.g. the grid's filter icon button).
		filterControl = null,
		trailingControl = null,
		iconKind
	} = props;

	return (
		<Box
			sx={{
				display: "flex",
				alignItems: "center",
				justifyContent: "space-between",
				gap: 2,
				background: "#FFFFFF",
				py: 1.5,
				minHeight: 64,
				pl: "10px",
				pr: 3
			}}
		>
			{/* Left side: title + search */}
			<Box sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: 0, flex: "0 1 auto" }}>
				<Stack direction="column" spacing={hasDescription ? 0.5 : 0} sx={{ minWidth: 0, flexShrink: 0 }}>
					<div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
						{iconKind && PAGE_TITLE_ICONS[iconKind] ? (
							<Box
								sx={{
									display: "inline-flex",
									alignItems: "center",
									justifyContent: "center",
									flexShrink: 0,
									"& svg": { width: 26, height: 26, display: "block" },
									"& svg path": { fill: "url(#navIconGradient)" }
								}}
							>
								<FontAwesomeIcon icon={PAGE_TITLE_ICONS[iconKind]} />
							</Box>
						) : null}
						<CoAppDataGridHeaderTitle variant="h1" color="text.primary" title={title} sx={{ m: 0 }}>
							{title}
						</CoAppDataGridHeaderTitle>
					</div>
					{hasDescription ? (
						<Typography variant="body2" sx={{ color: "rgba(13, 31, 48, 0.55)", maxWidth: 720 }}>
							{description}
						</Typography>
					) : null}
				</Stack>

				{searchIsPresent ? (
					<Box sx={{ width: 320, minWidth: 160, flexShrink: 1 }}>
						<CoAppTextField
							value={searchValue}
							onChange={(e) => onSearchChange?.(e.target.value)}
							placeholder={searchPlaceholder || `Search ${typeof title === "string" ? title.toLowerCase() : ""}`}
							size="small"
							margin="none"
							fullWidth
							slotProps={{
								input: {
									startAdornment: (
										<InputAdornment position="start">
											<Search sx={{ fontSize: 18, color: "rgba(13, 31, 48, 0.45)" }} />
										</InputAdornment>
									),
									endAdornment: searchValue ? (
										<InputAdornment position="end">
											<IconButton
												size="small"
												onClick={() => onSearchChange?.("")}
												edge="end"
												sx={{ color: "rgba(13, 31, 48, 0.40)", p: 0.25, "&:hover": { color: "rgba(13, 31, 48, 0.70)" } }}
											>
												<Close sx={{ fontSize: 16 }} />
											</IconButton>
										</InputAdornment>
									) : undefined
								}
							}}
						/>
					</Box>
				) : null}
				{filterControl ? (
					<Box sx={{ flexShrink: 0, display: "flex", alignItems: "center" }}>{filterControl}</Box>
				) : null}
			</Box>

			<Stack component="div" direction="row" spacing={1.5} sx={{ alignItems: "center", flexShrink: 0 }}>
				{editIsPresent ? (
					<CoAppIconTextButton
						icon={<Edit sx={{ fontSize: "16px", marginRight: "5px" }} />}
						text={`Edit ${resourceType} name`}
						onClick={editOnClickHandler}
					/>
				) : null}
				{deleteIsPresent ? (
					<CoAppIconTextButton
						icon={<Delete sx={{ fontSize: "16px", marginRight: "5px" }} />}
						text={`Delete ${resourceType}`}
						onClick={deleteOnClickHandler}
						isDestructive={true}
					/>
				) : null}
				{toggleIsPresent ? (
					<FormControlLabel
						control={<Switch checked={toggleStatus} onChange={toggleHandler} />}
						label={<Typography variant="body3" sx={{ fontSize: "12px", pt: "2px", pl: "2px" }}>{toggleStatus ? "Active" : "Inactive"}</Typography>}
						labelPlacement="start"
					/>
				) : null}
				{tagManagementIsPresent ? <TagManagement initRuleLibrary={initRuleLibrary} /> : null}
				{addIsPresent ? (
					<CoAppIconActionButton
						icon={<Add sx={{ fontSize: "16px", marginRight: "5px" }} />}
						text={`${resourceType === "Rule" ? "Build" : "Add"} ${subResourceType ? subResourceType : resourceType}`}
						onClick={addOnClickHandler}
					/>
				) : null}
				{trailingControl ? (
					<Box sx={{ flexShrink: 0, display: "flex", alignItems: "center" }}>{trailingControl}</Box>
				) : null}
			</Stack>
		</Box>
	);
}
